import React from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import {
    ExclamationTriangleIcon,
    DocumentArrowUpIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';

interface SavePromptDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSaveAndExit: () => void;
    onExitWithoutSaving: () => void;
    isSaving?: boolean;
}

export default function SavePromptDialog({
    isOpen,
    onClose,
    onSaveAndExit,
    onExitWithoutSaving,
    isSaving = false,
}: SavePromptDialogProps) {
    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />

            {/* Dialog container */}
            <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
                <DialogPanel className="relative max-w-md w-full bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>

                    {/* Content */}
                    <div className="p-6 pt-8">
                        {/* Icon and title */}
                        <div className="flex flex-col items-center text-center mb-6">
                            <div className="flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                                <ExclamationTriangleIcon className="h-8 w-8 text-amber-600" />
                            </div>
                            <DialogTitle className="text-xl font-semibold text-gray-900 mb-2">
                                Save changes before exiting?
                            </DialogTitle>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                You have unsaved changes to your experience.
                                Would you like to save them before exiting the
                                editor?
                            </p>
                        </div>

                        {/* Action buttons */}
                        <div className="space-y-3">
                            {/* Primary action - Save & Exit */}
                            <button
                                onClick={onSaveAndExit}
                                disabled={isSaving}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                                style={{
                                    width: 'calc(100% - 2rem)',
                                }}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <DocumentArrowUpIcon className="h-5 w-5" />
                                        <span>Save & Exit</span>
                                    </>
                                )}
                            </button>

                            {/* Secondary actions */}
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={onExitWithoutSaving}
                                    disabled={isSaving}
                                    className="px-4 py-3 text-center bg-red-50 text-red-700 rounded-xl font-medium hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                >
                                    Don't Save
                                </button>

                                <button
                                    onClick={onClose}
                                    disabled={isSaving}
                                    className="px-4 py-3 text-center bg-gray-50 text-gray-700 rounded-xl font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}
